import { useState } from 'react';
import { Search, X, UserPlus } from 'lucide-react';
import { useKundenSearch, KUNDEN_SUCHE_MINDESTLAENGE } from '@/hooks/useKundenSearch';
import type { KundeSuchTreffer } from '@/types/bestellung';
import { Spinner } from '@/components/ui/Spinner';
import { FOKUS_RING } from '@/lib/fokus';

type Props = {
  selected: KundeSuchTreffer | null;
  onSelect: (k: KundeSuchTreffer) => void;
  onClear: () => void;
  onStartNew: () => void;
};

export function KundenSearch({ selected, onSelect, onClear, onStartNew }: Props) {
  const [query, setQuery] = useState('');
  const search = useKundenSearch(query);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-line bg-cream-warm px-3 py-2">
        <span className="text-sm text-ink">{selected.anzeigename}</span>
        {selected.ort && <span className="text-xs text-ink-faint">· {selected.ort}</span>}
        <button
          type="button"
          onClick={onClear}
          className="touch-ziel ml-auto inline-flex h-8 w-8 items-center justify-center rounded text-ink-faint hover:bg-cream-dark hover:text-ink"
          aria-label="Kunden entfernen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  const showResults = query.trim().length >= KUNDEN_SUCHE_MINDESTLAENGE;
  // Treffer der vorigen Eingabe stehen lassen, solange die neue Suche
  // laeuft — ein leerer Kasten waere unruhiger als ein veralteter.
  const results = search.data?.kunden ?? [];
  // Enter waehlt den obersten Treffer (B50). Die Liste ist serverseitig
  // sortiert, der erste Eintrag ist also der beste — bei genau einem
  // Treffer ohnehin der einzige.
  const enterTreffer = !search.laeuft && results.length > 0 ? results[0] : null;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-ink-faint" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (enterTreffer !== null) onSelect(enterTreffer);
          }}
          placeholder="Kunden suchen (Name, Firma, Mail, BONUS-Nr.)"
          className={`min-h-[44px] w-full rounded-md border border-line bg-cream py-2.5 pl-10 pr-3 text-sm ${FOKUS_RING}`}
        />
      </div>

      {showResults && (
        <div className="rounded-md border border-line bg-cream">
          {/* "Suche …" ab dem ersten Zeichen, auch waehrend der
              Entprellung — vorher blieb der Kasten in diesen 300 ms bei
              "Keine Treffer." stehen und las sich wie ein Ergebnis
              (B49, B84). "Keine Treffer." sagt nur der Server. */}
          {search.laeuft && (
            <p className="flex items-center gap-2 px-3 py-3 text-sm text-ink-faint">
              <Spinner />
              Suche …
            </p>
          )}
          {!search.laeuft && results.length === 0 && (
            <p className="px-3 py-3 text-sm text-ink-faint">Keine Treffer.</p>
          )}
          {results.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => onSelect(k)}
              className={
                k.id === enterTreffer?.id
                  ? 'flex w-full min-h-[44px] items-center gap-3 bg-akzent-sanft px-3 py-2 text-left text-sm'
                  : 'flex w-full min-h-[44px] items-center gap-3 px-3 py-2 text-left text-sm hover:bg-cream-dark'
              }
            >
              <span className="text-ink">{k.anzeigename}</span>
              {k.ort && <span className="text-xs text-ink-faint">{k.ort}</span>}
              {k.bonus_kundennr && (
                <span className="text-xs text-ink-faint">#{k.bonus_kundennr}</span>
              )}
              {k.id === enterTreffer?.id && (
                <span className="ml-auto shrink-0 text-xs text-akzent-stark">Enter ↵</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* A6: Hier stand "Neuen Kunden anlegen". Das versprach einen
          Kundenstamm, den das Tracking nicht führt — Master ist BONUS.
          Was der Knopf wirklich tut, sagt der Badge in der Detailansicht
          hinterher: "Im Tracking erfasst — bitte später in BONUS
          nachpflegen". Beide Stellen sagen jetzt dasselbe. */}
      <button
        type="button"
        onClick={onStartNew}
        className="inline-flex min-h-[44px] items-center gap-2 text-sm text-akzent hover:text-akzent-stark"
      >
        <UserPlus className="h-4 w-4" />
        Kunde im Tracking erfassen
      </button>
    </div>
  );
}
